import { ErrorMessage, Field, useField } from 'formik'
import { FormControl, FormErrorMessage, Input } from '~/design_system'
import { useCustomReportBuilderStore } from '~/custom_reports/builder/state'
import { useReportSystemVariables } from '~/custom_reports/api'
import { useMsg } from '~/common/localizationUtils'
import VndlySelect from '../../components/VndlySelect'
import { isCalculatedField, isDatasetField, isParameterField, isSystemVariableField, isValueField } from '../shared'

export const DatasetInput = ({
  name,
  onChange,
  disabled = false,
  datasetFields = false,
  calculated = false,
  parameters = false,
  values = false,
  systemVariables = false,
  testid = name
}) => {
  const [, { touched, error }] = useField(name)
  const getDatasetOptions = useCustomReportBuilderStore(state => state.actions.getDatasetOptions)
  const options = getDatasetOptions(datasetFields, calculated, parameters, values, systemVariables)

  const extraProps = onChange ? { onChange } : {}

  return (
    <FormControl data-testid={testid} isInvalid={touched && error} isDisabled={disabled}>
      <Field name={name} component={VndlySelect} options={options} disabled={disabled} {...extraProps} />
      <FormErrorMessage>
        <ErrorMessage name={name} />
      </FormErrorMessage>
    </FormControl>
  )
}

export const DatasetFieldInput = ({
  field,
  name,
  options,
  predicate = null,
  exclude = [],
  selectedFields = null,
  disabled = false,
  onChange,
  testid = name
}) => {
  const [, { touched, error }] = useField(name)
  const actions = useCustomReportBuilderStore(state => state.actions)
  const { data: systemVariables } = useReportSystemVariables()
  const extraProps = onChange ? { onChange } : {}
  const msg = useMsg()

  let fieldComponent
  switch (true) {
    case isDatasetField(field): {
      fieldComponent = (
        <Field
          id={name}
          name={name}
          component={VndlySelect}
          disabled={disabled}
          options={options || actions.getDatasetFieldOptions(field, { predicate, exclude, selectedFields }, msg) || []}
          {...extraProps}
        />
      )
      break
    }
    case isCalculatedField(field): {
      fieldComponent = (
        <Field
          id={name}
          name={name}
          disabled={disabled}
          component={VndlySelect}
          options={actions.getCalculatedFieldOptions({ exclude, predicate }, msg)}
          {...extraProps}
        />
      )
      break
    }
    case isParameterField(field): {
      fieldComponent = (
        <Field
          id={name}
          name={name}
          disabled={disabled}
          component={VndlySelect}
          options={actions.getParameterFieldOptions({ exclude, predicate }, msg)}
        />
      )
      break
    }
    case isValueField(field): {
      fieldComponent = <Field id={name} name={name} isDisabled={disabled} as={Input} placeholder="Field Name" />
      break
    }
    case systemVariables && isSystemVariableField(field): {
      const options = systemVariables
        .filter(field => exclude.indexOf(field.id) === -1)
        .map(field => ({
          value: field.id,
          label: field.l10n_name_key ? msg({ id: field.l10n_name_key, ...(field.l10n_name_context || {}) }) : field.name
        }))
      fieldComponent = <Field id={name} name={name} disabled={disabled} component={VndlySelect} options={options} />
      break
    }
    default:
      fieldComponent = null
  }

  if (!fieldComponent) {
    return null
  }

  return (
    <FormControl data-testid={testid} isInvalid={touched && error} isDisabled={disabled}>
      {fieldComponent}
      <FormErrorMessage>
        <ErrorMessage name={name} />
      </FormErrorMessage>
    </FormControl>
  )
}
